import { useEffect, useRef, useState } from "react";

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const Reveal = ({ children, className = "", delay = 0, as: Tag = "div", variant = "up", ...rest }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || reduced()) return setShown(true);
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal reveal-${variant} ${shown ? "in" : ""} ${className}`} style={{ transitionDelay: `${delay}s` }} {...rest}>{children}</Tag>;
};

export const Parallax = ({ children, speed = 0.2, className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (reduced()) return;
    let raf;
    const tick = () => {
      const el = ref.current;
      if (el) {
        const r = el.parentElement.getBoundingClientRect();
        const off = (r.top + r.height / 2 - window.innerHeight / 2) * -speed;
        el.style.transform = `translate3d(0, ${off.toFixed(1)}px, 0)`;
      }
      raf = null;
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(tick); };
    tick();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { window.removeEventListener("scroll", on); window.removeEventListener("resize", on); if (raf) cancelAnimationFrame(raf); };
  }, [speed]);
  return <div ref={ref} className={`will-change-transform ${className}`}>{children}</div>;
};

export const Spotlight = ({ className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current?.parentElement;
    if (!el || reduced()) return;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
      ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
      ref.current.style.opacity = "1";
    };
    el.addEventListener("pointermove", move);
    return () => el.removeEventListener("pointermove", move);
  }, []);
  return <div ref={ref} aria-hidden className={`spotlight pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ${className}`} />;
};

export const Tilt = ({ children, className = "", max = 7 }) => {
  const ref = useRef(null);
  const move = (e) => {
    if (reduced() || e.pointerType !== "mouse") return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    ref.current.style.transform = `perspective(900px) rotateX(${(-y * max).toFixed(2)}deg) rotateY(${(x * max).toFixed(2)}deg)`;
  };
  const leave = () => { if (ref.current) ref.current.style.transform = ""; };
  return <div ref={ref} onPointerMove={move} onPointerLeave={leave} className={`tilt ${className}`}>{children}</div>;
};

export const CountUp = ({ value, from = 0, duration = 1200, className = "", testid }) => {
  const ref = useRef(null);
  const [n, setN] = useState(from);
  const target = Number(value) || 0;
  useEffect(() => {
    const el = ref.current;
    if (!el || reduced()) return setN(target);
    let raf;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const step = (t) => {
        const p = Math.min(1, (t - t0) / duration);
        setN(Math.round(from + (target - from) * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    });
    io.observe(el);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [target, duration, from]);
  return <span ref={ref} className={className} data-testid={testid}>{n}</span>;
};

export const ScrollProgress = () => {
  const ref = useRef(null);
  useEffect(() => {
    const on = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (ref.current) ref.current.style.transform = `scaleX(${h > 0 ? window.scrollY / h : 0})`;
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return <div ref={ref} data-testid="scroll-progress" className="fixed left-0 right-0 top-0 z-[70] h-[2px] origin-left bg-gradient-to-r from-[#00F5D4] via-[#FFB800] to-[#FF2E63]" style={{ transform: "scaleX(0)" }} />;
};

export const useAutoReveal = (dep, enabled = true) => {
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main || reduced() || !enabled) return;
    const timers = [];
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const el = e.target;
      io.unobserve(el);
      el.classList.add("in");
      setTimeout(() => { el.classList.remove("reveal", "reveal-up", "in"); el.style.transitionDelay = ""; }, 1300);
    }), { threshold: 0.08, rootMargin: "0px 0px -6% 0px" });
    const seen = new WeakSet();
    const scan = () => main.querySelectorAll(".panel, [data-reveal]").forEach((el) => {
      if (seen.has(el) || el.closest("[role=dialog]") || el.parentElement?.closest(".panel")) return;
      seen.add(el);
      const idx = Array.prototype.indexOf.call(el.parentElement.children, el);
      el.style.transitionDelay = `${(idx % 4) * 0.09}s`;
      el.classList.add("reveal", "reveal-up");
      io.observe(el);
      timers.push(setTimeout(() => { if (el.classList.contains("reveal") && el.getBoundingClientRect().top < window.innerHeight) el.classList.add("in"); }, 2500));
    });
    scan();
    const mo = new MutationObserver(scan);
    mo.observe(main, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); timers.forEach(clearTimeout); };
  }, [dep, enabled]);
};
